import React, { useEffect, useMemo } from 'react';
import { compose } from 'recompose';
import styled, { cx, StylesProps } from '../../../../../lib/styles';
import pillarArticlesBlockStyles from './PillarArticlesBlock_styles';
import { algoliaDataToRetrieve, ArticleItem } from '../../../../../lib/algolia/types';
import ArticleTileBlock, { ArticleTileColumn } from '../../../../../components/presentational/ArticleTileBlock/ArticleTileBlock';
import { useAlgoliaObjects } from '../../../../../lib/hooks/useAlgoliaObjects';
import { GetObjectsOptions, SearchOptions } from '@algolia/client-search'; // Import Algolia types
import SkeletonLoader from '../../../../../components/presentational/SkeletonLoader/SkeletonLoader';
import { cfImage, cfImageSrcset, truncateStringByWord } from '../../../../../lib/stringUtils';
import Spacer from '../../../../../components/presentational/Spacer';
import theme from "../../../../../lib/theme";
import _ from 'lodash';
import ArticleReelBlock from '../../../../../components/presentational/ArticleReelBlock/ArticleReelBlock';
import { getContrastText } from '../../../../../lib/colourUtilities';
import CampaignBanner, { CampaignBannerFields } from '../../../../../components/presentational/CampaignBanner';
const { mediaSizes } = theme;

export interface NavSection extends PillarLink {
	articles: { id: number }[];
	backgroundColor?: string;
	textColor?: string;
	category?: PillarLink;
	categories?: PillarLink;
	excluded_categories?: string[];
	excluded_tags?: string[];
	excluded_object_ids?: string[];
}

interface PillarLink {
	id: number;
	label: string;
	url: string;
	links?: PillarLink[];
	children?: NavSection[];
};

/**
 * The block model
 */
export interface PillarArticlesBlockData {
	type: 'pillar_articles_block';
	sections: NavSection[];
	hero_articles: { id: number }[];
	nav_sections: NavSection[];
	nav_sections_sidebar_posts: { id: number }[];
	// Optional promotional banner rendered directly beneath the hero articles.
	// Gated by the editor's `enabled` toggle (see CampaignBannerFields).
	campaignBanner?: CampaignBannerFields | null;
}

interface PillarArticlesBlockProps {
	block: PillarArticlesBlockData;
	styles: StylesProps<typeof pillarArticlesBlockStyles>;
}

const ALGOLIA_INDEX_NAME = 'production_media_starlord'; // Define your Algolia index name

interface TileImage {
	width: number;
	height: number;
	widths: number[];
	sizes: string;
}

// Per-layout image sizing so each hero tile fetches dimensions matching its
// rendered size (prevents upscaling a small crop into a large tile).
const PROGRESSIVE_IMAGES: Record<string, TileImage> = {
	split: { width: 1200, height: 750, widths: [480, 600, 900, 1200], sizes: '(max-width: 768px) 100vw, 600px' },
	overlay2: { width: 1200, height: 600, widths: [480, 600, 900, 1200], sizes: '(max-width: 768px) 100vw, 600px' },
	overlay3: { width: 1200, height: 600, widths: [480, 600, 900, 1200], sizes: '(max-width: 1200px) 100vw, 600px' },
	stacked3: { width: 1200, height: 900, widths: [480, 640, 900, 1200], sizes: '(max-width: 1200px) 50vw, 640px' },
	stacked4: { width: 1200, height: 750, widths: [400, 600, 900, 1200], sizes: '(max-width: 768px) 100vw, 600px' },
};

/**
 * PillarArticlesBlock component
 * @param block - The block data passed from Wordpress
 * @param styles - Styles for the component
 * @returns JSX.Element
 */
const PillarArticlesBlock = ({
	block,
	//@ts-ignore
	styles,
}: PillarArticlesBlockProps & StylesProps<typeof pillarArticlesBlockStyles>) => {
	const TOTAL_ARTICLES_PER_BLOCK = 5; // Total articles to fetch for hero and nav sections

	// This will store results for each nav section, including its children
	const fetchedNavSections: {
		section: NavSection;
		articles: ArticleItem[] | null;
		loading: boolean;
		error: Error | null,
		searchOptions: SearchOptions
	}[] = [];

	block.sections.forEach(navSection => {
		const sectionArticleIds = useMemo(() => navSection.articles.map(article => `post#${article.toString()}`), []);
		const navSectionSearchOptions = useMemo<GetObjectsOptions>(() => ({
			attributesToRetrieve: algoliaDataToRetrieve,
		}), []);

		const {
			data: navSectionArticles,
			loading: navSectionLoading,
			error: navSectionError,
		} = useAlgoliaObjects<ArticleItem>(sectionArticleIds, ALGOLIA_INDEX_NAME, navSectionSearchOptions);

		fetchedNavSections.push({
			section: navSection,
			articles: navSectionArticles,
			loading: navSectionLoading,
			error: navSectionError,
			searchOptions: navSectionSearchOptions,
		});
	});

	const heroArticles = fetchedNavSections.slice(0, 1)[0]?.articles || [];
	const pillarSections = fetchedNavSections.slice(1, fetchedNavSections.length - 1) || [];
	const sidebarReelArticles = fetchedNavSections.slice(fetchedNavSections.length - 1, fetchedNavSections.length)[0]?.articles || [];
	const articlesLoading = fetchedNavSections.some(section => section.loading);

	// //	DEBUGGING
	// useEffect(() => {
	// 	console.log('PillarArticlesBlock mounted with block:', { block, sidebarReelArticles, fetchedNavSections });
	// }, [block, sidebarReelArticles, fetchedNavSections]);


	const heroBackgroundColor = block.sections?.[0]?.backgroundColor || '';
	// Editor override wins; otherwise auto-contrast against the background.
	const heroTextColorOverride = block.sections?.[0]?.textColor || '';

	const renderProgressiveSplitTile = (article: ArticleItem, img: TileImage, textColor?: string) => (
		<a
			key={article.objectID}
			href={article.slug}
			className={`progressiveSplitTile ${styles.progressiveSplitTile}`}
		>
			<div className={`progressiveSplitImageWrap ${styles.progressiveSplitImageWrap}`}>
				<img
					src={cfImage(article.image, { width: img.width, height: img.height, fit: 'cover', gravity: 'center' })}
					srcSet={cfImageSrcset(article.image, img.widths, { fit: 'cover', gravity: 'center' })}
					sizes={img.sizes}
					alt={article.title}
					className={styles.progressiveSplitImage}
					loading="lazy"
					decoding="async"
				/>
			</div>
			<div className={`progressiveSplitContent ${styles.progressiveSplitContent}`}>
				<div className={`progressiveSplitTitle ${styles.progressiveSplitTitle}`} style={textColor ? { color: textColor } : undefined}>{article.title}</div>
				{article.excerpt && (
					<div className={`progressiveSplitExcerpt ${styles.progressiveSplitExcerpt}`} style={textColor ? { color: textColor } : undefined}>{article.excerpt}</div>
				)}
			</div>
		</a>
	);

	const renderProgressiveOverlayTile = (article: ArticleItem, img: TileImage) => (
		<a
			key={article.objectID}
			href={article.slug}
			className={`progressiveOverlayTile ${styles.progressiveOverlayTile}`}
		>
			<img
				src={cfImage(article.image, { width: img.width, height: img.height, fit: 'cover', gravity: 'center' })}
				srcSet={cfImageSrcset(article.image, img.widths, { fit: 'cover', gravity: 'center' })}
				sizes={img.sizes}
				alt={article.title}
				className={styles.progressiveOverlayImage}
				loading="lazy"
				decoding="async"
			/>
			<div className={`progressiveOverlayTitle ${styles.progressiveOverlayTitle}`}>
				{truncateStringByWord(article.title, 100)}
			</div>
		</a>
	);

	const renderProgressiveStackedTile = (article: ArticleItem, img: TileImage) => (
		<a
			key={article.objectID}
			href={article.slug}
			className={`progressiveStackedTile ${styles.progressiveStackedTile}`}
		>
			<div className={`progressiveStackedImageWrap ${styles.progressiveStackedImageWrap}`}>
				<img
					src={cfImage(article.image, { width: img.width, height: img.height, fit: 'cover', gravity: 'center' })}
					srcSet={cfImageSrcset(article.image, img.widths, { fit: 'cover', gravity: 'center' })}
					sizes={img.sizes}
					alt={article.title}
					className={styles.progressiveStackedImage}
					loading="lazy"
					decoding="async"
				/>
			</div>
			<div className={`progressiveStackedTitle ${styles.progressiveStackedTitle}`}>
				{truncateStringByWord(article.title, 80)}
			</div>
		</a>
	);

	// Existing 5-up hero grid, kept verbatim as the 5+ fallback.
	const renderHeroGrid = (articles: ArticleItem[]) => (
		<ArticleTileBlock
			cssClasses="hero-articles"
			blockConfig={{ showTitleLinks: false }}
			columns={
				[
					{
						width: '50%',
						columnConfig: { showColumnNav: false, showTileMeta: true },
						tileDirection: 'row',
						articles: articles
							.filter(a => a.image && a.image !== '')
							.slice(0, 1)
							.map(article => ({
								article: {
									...article,
									image: cfImage(article.image, { width: 800 }),
									title: truncateStringByWord(article.title, 80),
								},
								options: { textPosition: 'bottom', isCoverImage: false, tileWidth: '100%' },
								cssClasses: 'hero-article-col-1-tile',
							})) || [],
						cssClasses: `flex-grow-1 hero-article-col hero-article-col-1`,
					},
					{
						width: '50%',
						columnConfig: { showColumnNav: false, showTileMeta: false },
						tileDirection: 'row',
						articles: articles
							.filter(a => a.image && a.image !== '')
							.slice(1, 5)
							.map(article => ({
								article: {
									...article,
									image: cfImage(article.image, { width: 400 }),
									title: truncateStringByWord(article.title, 70),
								},
								options: { textPosition: 'bottom', isCoverImage: false, tileWidth: '50%' },
								cssClasses: 'hero-article-col-2-tile',
							})) || [],
						cssClasses: `hero-article-col hero-article-col-2`,
					},
				] as ArticleTileColumn[]
			}
		/>
	);

	// Hero layout is driven by the number of selected hero articles (1-5); 5+ falls
	// back to the existing hero grid. Background colour applies only to the single-
	// article layout — it shows behind the content half while the feature image
	// covers the other half.
	const ProgressiveHeroLayout = (articles: ArticleItem[], backgroundColor: string, textColorOverride: string) => {
		const splitTextColor = textColorOverride || (backgroundColor ? getContrastText(backgroundColor) : undefined);
		switch (articles.length) {
			case 1:
				return (
					<div
						className={`progressive progressive-1 ${styles.progressive} ${styles.progressiveOne}`}
						style={backgroundColor ? { backgroundColor } : undefined}
					>
						{renderProgressiveSplitTile(articles[0], PROGRESSIVE_IMAGES.split, splitTextColor)}
					</div>
				);
			case 2:
				return (
					<div className={`progressive progressive-2 ${styles.progressive} ${styles.progressiveTwo}`}>
						{articles.map(article => renderProgressiveOverlayTile(article, PROGRESSIVE_IMAGES.overlay2))}
					</div>
				);
			case 3:
				return (
					<div className={`progressive progressive-3 ${styles.progressive} ${styles.progressiveThree}`}>
						{renderProgressiveOverlayTile(articles[0], PROGRESSIVE_IMAGES.overlay3)}
						<div className={`progressiveThreeStack ${styles.progressiveThreeStack}`}>
							{articles.slice(1, 3).map(article => renderProgressiveStackedTile(article, PROGRESSIVE_IMAGES.stacked3))}
						</div>
					</div>
				);
			case 4:
				return (
					<div className={`progressive progressive-4 ${styles.progressive} ${styles.progressiveFour}`}>
						{articles.map(article => renderProgressiveStackedTile(article, PROGRESSIVE_IMAGES.stacked4))}
					</div>
				);
			default:
				return renderHeroGrid(articles);
		}
	};

	return (
		<Spacer space={0} align={'center'}>
			<div
				dangerouslySetInnerHTML={{
					__html: `
          <script>
            window.googletag = window.googletag || {cmd: []};
            googletag.cmd.push(function() {
              googletag.defineSlot('/21875548389/collection_sas_sticky', [[300, 600],[300, 250]], 'gpt-ad-21875548389-collection_sas_sticky-0').addService(googletag.pubads());
              googletag.pubads();
              googletag.enableServices();
            });
          </script>        
        `}}
			></div>
			<Spacer space={'s'} width={mediaSizes.xxl ?? '1200px'} justify={'center'} className={cx(`homepage-layout`, styles.homepageLayout)}>
				{(heroArticles && heroArticles.length > 0 && articlesLoading === false)
					? ProgressiveHeroLayout(heroArticles, heroBackgroundColor, heroTextColorOverride)
					: <SkeletonLoader loaderType={'FeatureTileBlock'} />}

				{block.campaignBanner && block.campaignBanner.enabled && block.campaignBanner.title && (
					<CampaignBanner
						kicker={block.campaignBanner.kicker}
						title={block.campaignBanner.title}
						subtitle={block.campaignBanner.subtitle}
						ctaLabel={block.campaignBanner.ctaLabel}
						ctaUrl={block.campaignBanner.ctaUrl}
						backgroundColor={block.campaignBanner.backgroundColor}
							placement={'pillar_articles'}
					/>
				)}

				<div className={cx(`layout-columns d-flex`, styles.layoutColumns)}>
					<div
						style={{ flex: `1 0 calc(900px - 1em)`, display: 'flex', flexDirection: 'column', gap: '1em' }}
					>
						{(pillarSections && pillarSections.length > 0) ?
							(
								<>
									{pillarSections.map((sectionData, index) => {
										const { section, articles, } = sectionData;
										const { categories: categoryTree } = section;

										if (articles && articles.length > 0) {
											return (
												<div key={`nav-section-${index}`}>
													<ArticleTileBlock
														key={`nav-section-${index}`} // Use a unique key
														title={{
															label: categoryTree ? categoryTree.label : '',
															url: categoryTree ? categoryTree.url : '',
														}}
														headingLinks={categoryTree?.links || []} // Use section links for the heading
														subCategories={categoryTree?.children || []}
														blockConfig={{ showTitleLinks: true }} // Show title links for this block
														columns={
															[
																{
																	width: '60%',
																	tileDirection: 'row',
																	columnConfig: {
																		showTileMeta: true,
																	},
																	articles: articles
																		.filter(a => a.image && a.image !== '')
																		.slice(0, 1)
																		.map(article => {
																			return {
																				article: {
																					...article,
																					image: cfImage(article.image, { width: 800 }),
																					title: truncateStringByWord(article.title, 80),
																				},
																				options: {
																					textPosition: 'bottom',
																					isCoverImage: false,
																					tileWidth: '100%',
																				},
																				cssClasses: 'nav-section-col-1-tile',
																			};
																		}) || [],
																	cssClasses: `flex-grow-1 nav-section-col nav-section-col-1`,
																},
																{
																	width: '40%',
																	tileDirection: 'column',
																	columnConfig: {
																		showColumnNav: true,
																		showTileMeta: false,
																	},
																	articles: articles
																		.filter(a => a.image && a.image !== '')
																		.slice(1)
																		.map(article => {
																			return {
																				article: {
																					...article,
																					image: cfImage(article.image, { width: 400 }),
																					title: truncateStringByWord(article.title, 80),
																				},
																				options: {
																					textPosition: 'right',
																					tileWidth: '100%',
																				},
																				cssClasses: 'nav-section-col-2-tile',
																			};
																		}) || [],
																	cssClasses: `nav-section-col nav-section-col-2`,
																},
															] as ArticleTileColumn[]
														}
													/>
												</div>
											);
										}
										return null; // Skip rendering if no articles
									})}
								</>
							) : <SkeletonLoader loaderType={'TileBlock'} />}
					</div>
					<div
						style={{
							flex: `0 0 300px`,
							display: 'flex',
							flexDirection: 'column',
							gap: '0.5em',
							position: 'relative',
							justifyContent: 'flex-start',
						}}
					>
						{(sidebarReelArticles && sidebarReelArticles.length > 0) && (
							<>
								{articlesLoading === true ? (
									<SkeletonLoader loaderType={'TileBlock'} />
								) : (
									<ArticleReelBlock
										articles={sidebarReelArticles}
										styles={styles}
									/>
								)}
							</>
						)}
						<div className={cx(`sidebar-ad-container d-flex flex-column`)}
							style={{
								position: 'sticky',
								top: '10px',
								gap: '0.5em',
							}}>
							<div className={cx(`ad`, styles.ad)}>
								<div id="gpt-ad-21875548389-collection_sas_sticky-0"
									style={{ minWidth: '300px', minHeight: '250px' }}
								>
									<div
										dangerouslySetInnerHTML={{
											__html: `
                      <script>
                        googletag.cmd.push(function() { googletag.display('gpt-ad-21875548389-collection_sas_sticky-0'); });
                      </script>
                    ` }}
									></div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Spacer>
		</Spacer>
	);
};

export default compose(styled(pillarArticlesBlockStyles))(PillarArticlesBlock);